import { callInternal } from './internal.js';

const SERVICE = 'examine';

export interface ExamineMission {
	mission_id: string;
	title?: string;
	mission_name?: string;
	executor_type?: string;
	frequencg?: string;
	template_id?: string;
	template_name?: string;
	start_time?: string;
	end_time?: string;
	cycle_type?: string;
	frequency?: string;
	store_count?: string;
	store_done_count?: string;
	progress?: string;
	submit_count?: string;
	stores?: { store_id: string; store_name: string }[];
	users?: { user_id: string; user_name: string }[];
	[k: string]: unknown;
}

export async function fetchMissions(opts: {
	exceedStatus?: '0' | '1';
	keyword?: string;
} = {}): Promise<ExamineMission[]> {
	const query: Record<string, string | number> = {
		exceed_status: opts.exceedStatus ?? '0',
		is_site: '2',
	};
	if (opts.keyword) query.keyword = opts.keyword;
	const res = await callInternal<ExamineMission[] | { items: ExamineMission[] }>({
		short_name: SERVICE,
		action: '/mission/all',
		query,
		av: '2',
	});
	if (res.code !== 0) return [];
	const data = res.data;
	if (Array.isArray(data)) return data;
	if (data && typeof data === 'object' && 'items' in data) return (data as { items: ExamineMission[] }).items ?? [];
	return [];
}

export interface ExamineReport {
	examine_id?: string;
	id?: string;
	draft_id?: string;
	title?: string;
	store_id?: string;
	store_name?: string;
	store_code?: string;
	user_id?: string;
	user_name?: string;
	real_name?: string;
	score?: string;
	score_rate?: string;
	total_score?: string;
	origin_total_score?: string;
	judge_score?: string;
	judge_total_score?: string;
	judge_origin_total_score?: string;
	type?: string;
	platform?: string;
	create_time?: string;
	start_examine?: string;
	end_examine?: string;
	template_id?: string;
	template_title?: string;
	template_name?: string;
	mission_title?: string;
	mission_id?: string;
	audit_status?: string;
	auditor?: string;
	auditor_name?: string;
	submitter?: string;
	submitter_name?: string;
	create_time_f?: string;
	last_update_time?: string;
	last_update_time_f?: string;
	start_examine_f?: string;
	end_examine_f?: string;
	pic_item_count?: string;
	item_count?: string;
	screenshot?: string;
	mission_userids?: string;
	[k: string]: unknown;
}

export interface ExamineReportPage {
	items: ExamineReport[];
	total: number;
	currentCount: number;
	pageCount: number;
	currentPage: number;
	nextPage?: string;
}

export async function fetchExamineReports(opts: { page?: number; count?: number } = {}): Promise<ExamineReport[]> {
	const page = await fetchExamineReportPage(opts);
	return page.items;
}

function numberFrom(value: unknown): number {
	const n = Number(value ?? 0);
	return Number.isFinite(n) ? n : 0;
}

function stringFrom(value: unknown): string {
	if (value === null || value === undefined) return '';
	return String(value);
}

function itemsFrom<T>(data: unknown): T[] {
	if (Array.isArray(data)) return data as T[];
	if (!data || typeof data !== 'object') return [];
	const record = data as Record<string, unknown>;
	if (Array.isArray(record.items)) return record.items as T[];
	if (Array.isArray(record.data)) return record.data as T[];
	if (Array.isArray(record.list)) return record.list as T[];
	return [];
}

export interface AssessmentCounts {
	allExamineCount: number;
	storeExamineCount: number;
	myExamineCount: number;
}

export interface MyMissionCount {
	missionAll: number;
	missionHand: number;
}

export interface AssessmentMissionPage {
	items: ExamineMission[];
	total: number;
	currentCount: number;
	pageCount: number;
	currentPage?: number;
	nextPage?: string;
	currentCursor?: string;
	nextCursor?: string;
}

export interface AssessmentMissionStore {
	store_id: string;
	store_name?: string;
	store?: string;
	[k: string]: unknown;
}

export interface AssessmentMissionUser {
	user_id: string;
	user_name?: string;
	real_name?: string;
	[k: string]: unknown;
}

export interface AssessmentTemplateOption {
	template_id?: string;
	id?: string;
	title?: string;
	template_title?: string;
	template_name?: string;
	name?: string;
	[k: string]: unknown;
}

export interface AssessmentRankingPage {
	items: ExamineReport[];
	total: number;
	currentCount: number;
	pageCount: number;
	currentPage: number;
	nextPage?: string;
}

export type AssessmentManagementTab = 'report' | 'mine' | 'audits' | 'drafts';

export interface AssessmentManagementCounts {
	reportCount: number;
	myAssessmentCount: number;
	auditCount: number;
	draftCount: number;
}

export interface AssessmentManagementQuery {
	tab: AssessmentManagementTab;
	page?: number;
	count?: number;
	userId?: string;
	storeIds?: string[];
	startTime?: string;
	endTime?: string;
	auditStatus?: string;
	templateIds?: string[];
	reviewerIds?: string[];
	submitterIds?: string[];
}

export interface AssessmentDetailPic {
	url?: string;
	file?: string;
	path?: string;
	[k: string]: unknown;
}

export interface AssessmentDetailItem {
	title?: string;
	explanation?: string;
	remark?: string;
	score?: string | number;
	total_score?: string | number;
	value_type?: string;
	ignore?: string | number;
	status?: string | number;
	pic_list?: AssessmentDetailPic[];
	criterion_image?: AssessmentDetailPic[];
	event_id?: string;
	event_status?: string;
	[k: string]: unknown;
}

export interface AssessmentDetailContent {
	title?: string;
	items?: AssessmentDetailItem[];
	event?: unknown[];
	categories_id?: string;
	item_type?: string;
	red_line?: string | number;
	red_scope?: string | number;
	[k: string]: unknown;
}

export interface AssessmentDetailData {
	audit_status?: string;
	content?: AssessmentDetailContent[];
	create_time?: string;
	start_examine?: string;
	start_examine_f?: string;
	end_examine?: string;
	end_examine_f?: string;
	examine_id?: string;
	mission_title?: string;
	real_name?: string;
	platform?: string;
	score?: string | number;
	total_score?: string | number;
	store_name?: string;
	template_id?: string;
	title?: string;
	type?: string;
	[k: string]: unknown;
}

export async function fetchAssessmentCounts(): Promise<AssessmentCounts> {
	const res = await callInternal<{
		all_examine_count?: string | number;
		store_examine_count?: string | number;
		my_examine_count?: string | number;
	}>({
		short_name: SERVICE,
		action: '/store/examine/count',
		method: 'GET',
		av: '2',
	});
	if (res.code !== 0 || !res.data) {
		return { allExamineCount: 0, storeExamineCount: 0, myExamineCount: 0 };
	}
	return {
		allExamineCount: numberFrom(res.data.all_examine_count),
		storeExamineCount: numberFrom(res.data.store_examine_count),
		myExamineCount: numberFrom(res.data.my_examine_count),
	};
}

export async function fetchMyMissionCount(): Promise<MyMissionCount> {
	const res = await callInternal<{
		mission_all?: string | number;
		mission_hand?: string | number;
	}>({
		short_name: SERVICE,
		action: '/user/myMission',
		method: 'GET',
		av: '2',
	});
	if (res.code !== 0 || !res.data) return { missionAll: 0, missionHand: 0 };
	return {
		missionAll: numberFrom(res.data.mission_all),
		missionHand: numberFrom(res.data.mission_hand),
	};
}

export async function fetchMyMissionCursor(opts: {
	cursorId?: string;
	userId?: string;
	showExpired?: boolean;
	count?: number;
} = {}): Promise<AssessmentMissionPage> {
	const query: Record<string, string | number> = {
		cursorId: opts.cursorId ?? '0',
		count: opts.count ?? 10,
	};
	if (opts.userId) query.user_id = opts.userId;
	if (!opts.showExpired) query.exceed_status = 3;
	const res = await callInternal<{
		items?: ExamineMission[];
		total_count?: string | number;
		current_count?: string | number;
		page_count?: string | number;
		current_cursor?: string;
		next_cursor?: string;
	}>({
		short_name: SERVICE,
		action: '/business/mission/cursor',
		method: 'GET',
		query,
		av: '3',
	});
	if (res.code !== 0 || !res.data) {
		return { items: [], total: 0, currentCount: 0, pageCount: 0, currentCursor: opts.cursorId ?? '0' };
	}
	const items = Array.isArray(res.data.items) ? res.data.items : [];
	return {
		items,
		total: numberFrom(res.data.total_count ?? items.length),
		currentCount: numberFrom(res.data.current_count ?? items.length),
		pageCount: numberFrom(res.data.page_count ?? (items.length > 0 ? 1 : 0)),
		currentCursor: res.data.current_cursor,
		nextCursor: res.data.next_cursor,
	};
}

export async function fetchRankingTasks(opts: {
	count?: number;
	page?: string | number;
} = {}): Promise<AssessmentMissionPage> {
	const res = await callInternal<{
		items?: ExamineMission[];
		total_count?: string | number;
		current_count?: string | number;
		page_count?: string | number;
		current_page?: string | number;
		next_page?: string;
	}>({
		short_name: SERVICE,
		action: '/business/mission/page',
		method: 'GET',
		query: {
			count: opts.count ?? 10,
			page: opts.page ?? '',
		},
		av: '3',
	});
	if (res.code !== 0 || !res.data) {
		return { items: [], total: 0, currentCount: 0, pageCount: 0 };
	}
	const items = Array.isArray(res.data.items) ? res.data.items : [];
	return {
		items,
		total: numberFrom(res.data.total_count ?? items.length),
		currentCount: numberFrom(res.data.current_count ?? items.length),
		pageCount: numberFrom(res.data.page_count ?? (items.length > 0 ? 1 : 0)),
		currentPage: numberFrom(res.data.current_page),
		nextPage: res.data.next_page,
	};
}

export async function fetchMissionStores(missionId: string): Promise<AssessmentMissionStore[]> {
	if (!missionId) return [];
	const res = await callInternal<unknown>({
		short_name: SERVICE,
		action: '/mission/store/all',
		method: 'GET',
		query: { mission_id: missionId },
		av: '2',
	});
	return res.code === 0 ? itemsFrom<AssessmentMissionStore>(res.data) : [];
}

export async function fetchMissionUsers(missionId: string): Promise<AssessmentMissionUser[]> {
	if (!missionId) return [];
	return fetchAssessmentFilterUsers(missionId);
}

export async function fetchAssessmentFilterUsers(missionId?: string): Promise<AssessmentMissionUser[]> {
	const query: Record<string, string> = {};
	if (missionId) query.mission_id = missionId;
	const res = await callInternal<unknown>({
		short_name: SERVICE,
		action: '/user/getExecutor',
		method: 'GET',
		...(Object.keys(query).length > 0 ? { query } : {}),
		av: '2',
	});
	return res.code === 0 ? itemsFrom<AssessmentMissionUser>(res.data) : [];
}

export async function fetchAssessmentTemplates(): Promise<AssessmentTemplateOption[]> {
	const res = await callInternal<unknown>({
		short_name: SERVICE,
		action: '/business/examine/template',
		method: 'GET',
		av: '2',
	});
	return res.code === 0 ? itemsFrom<AssessmentTemplateOption>(res.data) : [];
}

export async function fetchAssessmentRanking(opts: {
	page?: number;
	count?: number;
	storeIds?: string[];
	missionIds?: string[];
	startDate: string;
	endDate: string;
	sort?: '0' | '1';
	userIds?: string[];
}): Promise<AssessmentRankingPage> {
	const currentPage = opts.page ?? 1;
	const query: Record<string, string | number> = {
		count: opts.count ?? 10,
		page: currentPage,
		start_time: `${opts.startDate} 00:00:00`,
		end_time: `${opts.endDate} 23:59:59`,
		sort: opts.sort ?? '0',
	};
	if (opts.storeIds && opts.storeIds.length > 0) query.store_ids = opts.storeIds.join(',');
	if (opts.missionIds && opts.missionIds.length > 0) query.mission_ids = opts.missionIds.join(',');
	if (opts.userIds && opts.userIds.length > 0) query.user_id = opts.userIds.join(',');
	const res = await callInternal<{
		items?: ExamineReport[];
		total_count?: string | number;
		current_count?: string | number;
		page_count?: string | number;
		current_page?: string | number;
		next_page?: string;
	}>({
		short_name: SERVICE,
		action: '/data/examine/page',
		method: 'GET',
		query,
		av: '2',
	});
	if (res.code !== 0 || !res.data) {
		return { items: [], total: 0, currentCount: 0, pageCount: 0, currentPage };
	}
	const items = Array.isArray(res.data.items) ? res.data.items : [];
	return {
		items,
		total: numberFrom(res.data.total_count ?? items.length),
		currentCount: numberFrom(res.data.current_count ?? items.length),
		pageCount: numberFrom(res.data.page_count ?? (items.length > 0 ? 1 : 0)),
		currentPage: numberFrom(res.data.current_page ?? currentPage),
		nextPage: stringFrom(res.data.next_page) || undefined,
	};
}

export async function fetchAssessmentManagementCounts(): Promise<AssessmentManagementCounts> {
	const res = await callInternal<{
		examine_count?: string | number;
		my_examine_count?: string | number;
		audit_count?: string | number;
		draft_count?: string | number;
	}>({
		short_name: SERVICE,
		action: '/i/examine/count',
		method: 'GET',
		av: '3',
	});
	if (res.code !== 0 || !res.data) {
		return { reportCount: 0, myAssessmentCount: 0, auditCount: 0, draftCount: 0 };
	}
	return {
		reportCount: numberFrom(res.data.examine_count),
		myAssessmentCount: numberFrom(res.data.my_examine_count),
		auditCount: numberFrom(res.data.audit_count),
		draftCount: numberFrom(res.data.draft_count),
	};
}

function addJoined(body: Record<string, unknown>, key: string, values?: string[]) {
	if (!values || values.length === 0) return;
	const text = values.map((item) => String(item).trim()).filter(Boolean).join(',');
	if (text) body[key] = text;
}

function buildManagementBody(opts: AssessmentManagementQuery): Record<string, unknown> {
	const body: Record<string, unknown> = {
		page: opts.page ?? 1,
		count: opts.count ?? 20,
	};
	addJoined(body, 'store_ids', opts.storeIds);
	addJoined(body, 'template_ids', opts.templateIds);
	if (opts.startTime) body.start_time = opts.startTime;
	if (opts.endTime) body.end_time = opts.endTime;

	if (opts.tab === 'report') {
		body.audit_status = opts.auditStatus ?? '1';
		addJoined(body, 'auditor_id', opts.reviewerIds);
		addJoined(body, 'user_id', opts.submitterIds);
		return body;
	}

	if (opts.tab === 'mine') {
		if (opts.userId) body.user_id = opts.userId;
		if (opts.auditStatus) body.audit_status = opts.auditStatus;
		return body;
	}

	if (opts.tab === 'audits') {
		if (opts.auditStatus) body.audit_status = opts.auditStatus;
		return body;
	}

	return body;
}

function managementAction(tab: AssessmentManagementTab): string {
	if (tab === 'audits') return '/examine/audit/page';
	if (tab === 'drafts') return '/examine/draft/page';
	return '/examine/page';
}

export async function fetchAssessmentManagementPage(opts: AssessmentManagementQuery): Promise<ExamineReportPage> {
	const currentPage = opts.page ?? 1;
	const res = await callInternal<{
		items?: ExamineReport[];
		total_count?: string | number;
		current_count?: string | number;
		page_count?: string | number;
		current_page?: string | number;
		next_page?: string;
	}>({
		short_name: SERVICE,
		action: managementAction(opts.tab),
		method: 'POST',
		body: buildManagementBody(opts),
		av: '2',
	});
	if (res.code !== 0 || !res.data) {
		return { items: [], total: 0, currentCount: 0, pageCount: 0, currentPage };
	}
	const items = Array.isArray(res.data.items) ? res.data.items : [];
	return {
		items,
		total: numberFrom(res.data.total_count ?? items.length),
		currentCount: numberFrom(res.data.current_count ?? items.length),
		pageCount: numberFrom(res.data.page_count ?? (items.length > 0 ? 1 : 0)),
		currentPage: numberFrom(res.data.current_page ?? currentPage),
		nextPage: stringFrom(res.data.next_page) || undefined,
	};
}

export async function fetchAssessmentDetail(examineId: string): Promise<AssessmentDetailData | null> {
	if (!examineId) return null;
	const res = await callInternal<AssessmentDetailData | { data?: AssessmentDetailData }>({
		short_name: SERVICE,
		action: '/store/examine',
		method: 'GET',
		query: {
			examine_id: examineId,
			event_link: '1',
		},
		av: '2',
	});
	if (res.code !== 0 || !res.data) return null;
	const nested = (res.data as { data?: AssessmentDetailData }).data;
	const data = nested && typeof nested === 'object' ? nested : res.data;
	return data && typeof data === 'object' ? (data as AssessmentDetailData) : null;
}

export async function fetchExamineReportPage(opts: { page?: number; count?: number } = {}): Promise<ExamineReportPage> {
	const currentPage = opts.page ?? 1;
	const res = await callInternal<ExamineReport[] | {
		items?: ExamineReport[];
		total_count?: string | number;
		current_count?: string | number;
		page_count?: string | number;
		current_page?: string | number;
		next_page?: string;
	}>({
		short_name: SERVICE,
		action: '/examine/page',
		query: {
			page: currentPage,
			count: opts.count ?? 30,
		},
		av: '1',
	});
	if (res.code !== 0) {
		throw new Error(`${res.msg || 'request failed'} (code=${res.code})`);
	}
	const data = res.data;
	if (Array.isArray(data)) {
		return {
			items: data,
			total: data.length,
			currentCount: data.length,
			pageCount: data.length > 0 ? 1 : 0,
			currentPage,
		};
	}
	if (data && typeof data === 'object') {
		const items = Array.isArray(data.items) ? data.items : [];
		return {
			items,
			total: numberFrom(data.total_count ?? items.length),
			currentCount: numberFrom(data.current_count ?? items.length),
			pageCount: numberFrom(data.page_count ?? (items.length > 0 ? 1 : 0)),
			currentPage: numberFrom(data.current_page ?? currentPage),
			nextPage: data.next_page,
		};
	}
	return {
		items: [],
		total: 0,
		currentCount: 0,
		pageCount: 0,
		currentPage,
	};
}

export async function fetchMissionDetail(mission_id: string): Promise<ExamineMission | null> {
	const res = await callInternal<ExamineMission>({
		short_name: SERVICE,
		action: '/business/mission',
		query: { mission_id },
	});
	return res.code === 0 ? (res.data ?? null) : null;
}
